import { useState } from "react";
import { ScrollText, ChevronDown, UserCheck } from "lucide-react";

/**
 * Bloc « Instructions » d'un spot (consignes du magasin / du directeur).
 * Affiché sur CHAQUE carte de planning (collaborateur RD, référent, responsable) là où
 * la personne est affectée. UX : discret mais bien visible (certaines consignes sont
 * FERMES). Texte long → replié sur 2 lignes avec « Lire tout / Réduire ».
 *
 * Props :
 *  - text : la consigne (string). Ne rend rien si vide.
 *  - testid : data-testid de base (optionnel).
 *  - dense : version compacte (paddings réduits) pour les grilles denses.
 */
export const SpotInstructions = ({ text, testid, dense = false }) => {
  const [open, setOpen] = useState(false);
  const clean = (text || "").trim();
  if (!clean) return null;
  const isLong = clean.length > 140;
  const shown = open || !isLong ? clean : `${clean.slice(0, 140).replace(/\s+\S*$/, "")}…`;
  return (
    <div
      data-testid={testid}
      className={`mt-2 rounded-lg border border-[rgba(238,114,46,0.25)] bg-[rgba(252,219,83,0.16)] ${dense ? "px-2 py-1" : "px-2.5 py-1.5"}`}
    >
      <div className="flex items-start gap-1.5">
        <ScrollText className="mt-[1px] h-3.5 w-3.5 shrink-0 text-[var(--shariti-orange)]" />
        <div className="min-w-0 flex-1">
          <div className="text-[9px] font-extrabold uppercase tracking-wide text-[#8A3A10]">
            Instructions
          </div>
          <p className="mt-0.5 whitespace-pre-line break-words text-[11px] leading-relaxed text-ink">
            {shown}
          </p>
          {isLong && (
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              data-testid={testid ? `${testid}-toggle` : undefined}
              className="mt-0.5 inline-flex items-center gap-0.5 text-[10px] font-bold text-[var(--shariti-orange)] transition-colors hover:underline"
              aria-expanded={open}
            >
              {open ? "Réduire" : "Lire tout"}
              <ChevronDown className={`h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Bloc « Autorisé par » : le responsable du magasin ayant autorisé la venue.
 * Indispensable pour savoir à qui se présenter à l'accueil le jour J.
 * Props : auth = { name, role, email } (ne rend rien sans nom), testid.
 */
export const SpotAuthorizedBy = ({ auth, testid }) => {
  const name = (auth?.name || "").trim();
  if (!name) return null;
  const role = (auth?.role || "").trim();
  return (
    <div
      data-testid={testid}
      className="mt-1.5 flex items-start gap-1.5 rounded-lg border border-[var(--border-soft)] bg-[var(--surface-1)] px-2 py-1"
    >
      <UserCheck className="mt-[1px] h-3.5 w-3.5 shrink-0" style={{ color: "#2E8B67" }} />
      <div className="min-w-0 text-[11px] leading-snug">
        <span className="text-muted-ink">Autorisé par </span>
        <span className="break-words font-bold text-ink">{name}</span>
        {role && <span className="text-muted-ink"> · {role}</span>}
      </div>
    </div>
  );
};

export default SpotInstructions;
